import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './ErrorCorrectionPage.css';

/**
 * A component for addressing validation errors in a dataset, providing options
 * to edit, revalidate, or skip invalid entries. Displays a list of errors and
 * allows users to make corrections to the data rows.
 *
 * @param {Object} params The properties object.
 * @param {string} params.jobId The unique identifier for the validation job.
 * @param {Object} params.validationData The validation results containing errors and original data.
 * @param {Array} [params.validationData.errors] A list of error objects with details about invalid rows.
 * @param {Function} params.setValidationData Function to update the validation data state.
 * @return {JSX.Element} Returns the rendered error correction interface, or a message if no errors exist.
 */
function ErrorCorrectionPage({ jobId, validationData, setValidationData }) {
  const navigate = useNavigate();
  const [errors, setErrors] = useState(validationData?.errors || []);
  const [editingRows, setEditingRows] = useState({});
  const [isRevalidating, setIsRevalidating] = useState(false);

  // Sync errors state with validationData when it changes
  useEffect(() => {
    if (validationData?.errors) {
      console.log('ErrorCorrectionPage - useEffect: updating errors from validationData');
      setErrors(validationData.errors);
    }
  }, [validationData]);

  // Debug: Log what we received
  console.log('ErrorCorrectionPage - validationData:', validationData);
  console.log('ErrorCorrectionPage - validationData.errors:', validationData?.errors);
  console.log('ErrorCorrectionPage - errors state:', errors);
  if (errors.length > 0) {
    console.log('ErrorCorrectionPage - first error:', errors[0]);
    console.log('ErrorCorrectionPage - first error original_data:', errors[0]?.original_data);
  }

  const handleEdit = (rowNumber, field, value) => {
    setEditingRows(prev => ({
      ...prev,
      [rowNumber]: {
        ...prev[rowNumber],
        [field]: value
      }
    }));
  };

  const handleRevalidate = async (rowNumber) => {
    setIsRevalidating(true);
    try {
      const editedData = editingRows[rowNumber];
      if (!editedData) return;

      const originalRow = errors.find(e => e.row_number === rowNumber);
      const updatedRow = {
        ...originalRow.original_data,
        ...editedData,
        row_number: rowNumber
      };

      await apiService.revalidateAddresses(jobId, [updatedRow]);

      // Refresh validation data
      const response = await apiService.getValidationProgress(jobId);
      setValidationData(response.data);
      setErrors(response.data.errors || []);

      // Clear editing state for this row
      setEditingRows(prev => {
        const newState = { ...prev };
        delete newState[rowNumber];
        return newState;
      });
    } catch (error) {
      console.error('Error revalidating:', error);
      alert('Failed to revalidate address. Please try again.');
    } finally {
      setIsRevalidating(false);
    }
  };

  const handleRevalidateAll = async () => {
    setIsRevalidating(true);
    try {
      const allUpdatedRows = errors.map(error => {
        const edits = editingRows[error.row_number] || {};
        return {
          ...error.original_data,
          ...edits,
          row_number: error.row_number
        };
      });

      await apiService.revalidateAddresses(jobId, allUpdatedRows);

      // Refresh validation data
      const response = await apiService.getValidationProgress(jobId);

      if (response.data.errors && response.data.errors.length === 0) {
        // All errors resolved - go to Thnks selection
        setValidationData({
          ...response.data,
          results: response.data.results || []
        });
        navigate('/select-thnks');
      } else {
        setValidationData(response.data);
        setErrors(response.data.errors || []);
        setEditingRows({});
      }
    } catch (error) {
      console.error('Error revalidating all:', error);
      alert('Failed to revalidate addresses. Please try again.');
    } finally {
      setIsRevalidating(false);
    }
  };

  const handleSkip = async () => {
    setIsRevalidating(true);
    try {
      const rowNumbers = errors.map(e => e.row_number);
      await apiService.skipInvalidAddresses(jobId, rowNumbers);

      // Refresh validation data and go to Thnks selection
      const response = await apiService.getValidationProgress(jobId);
      setValidationData({
        ...response.data,
        results: response.data.results || []
      });

      navigate('/select-thnks');
    } catch (error) {
      console.error('Error skipping addresses:', error);
      alert('Failed to skip addresses. Please try again.');
    } finally {
      setIsRevalidating(false);
    }
  };

  // If no errors to display, show a message
  if (!errors || errors.length === 0) {
    return (
      <div className="error-correction-page">
        <div className="page-header">
          <h1>No Errors Found</h1>
          <p className="page-description">
            All addresses have been validated successfully!
          </p>
        </div>
        <div className="action-buttons">
          <button onClick={() => navigate('/select-thnks')} className="btn-primary">
            Continue to Select Thnks
          </button>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="error-correction-page">
      <div className="page-header">
        <h1>Address Corrections Needed</h1>
        <p className="page-description">
          {errors.length} address{errors.length !== 1 ? 'es' : ''} require attention.
          Please review and correct the highlighted fields.
        </p>
      </div>

      {/* Show each error as a card for better editing */}
      <div className="error-cards">
        {errors.map(error => {
          const data = error.original_data || {};
          const edits = editingRows[error.row_number] || {};
          const hasError = (field) => error.error_fields?.includes(field);

          return (
            <div key={error.row_number} className="error-card">
              <div className="error-card-header">
                <span className="row-badge">Row {error.row_number}</span>
                <span className="recipient-name">
                  {data.first_name} {data.last_name}
                </span>
              </div>

              <div className="error-message-banner">
                <span className="error-icon">⚠️</span>
                <span className="error-text">{error.error_message || 'Address validation failed'}</span>
              </div>

              <div className="address-form">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Address Line 1 {hasError('address_1') && <span className="field-error">*</span>}</label>
                    <input
                      type="text"
                      value={edits.address_1 ?? data.address_1 ?? ''}
                      onChange={(e) => handleEdit(error.row_number, 'address_1', e.target.value)}
                      className={hasError('address_1') ? 'error' : ''}
                      placeholder="Street address"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      value={edits.address_2 ?? data.address_2 ?? ''}
                      onChange={(e) => handleEdit(error.row_number, 'address_2', e.target.value)}
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>
                </div>

                <div className="form-row three-col">
                  <div className="form-group">
                    <label>City {hasError('city') && <span className="field-error">*</span>}</label>
                    <input
                      type="text"
                      value={edits.city ?? data.city ?? ''}
                      onChange={(e) => handleEdit(error.row_number, 'city', e.target.value)}
                      className={hasError('city') ? 'error' : ''}
                      placeholder="City"
                    />
                  </div>
                  <div className="form-group">
                    <label>State {hasError('state') && <span className="field-error">*</span>}</label>
                    <input
                      type="text"
                      value={edits.state ?? data.state ?? ''}
                      onChange={(e) => handleEdit(error.row_number, 'state', e.target.value)}
                      className={hasError('state') ? 'error' : ''}
                      placeholder="State"
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code {hasError('zip_code') && <span className="field-error">*</span>}</label>
                    <input
                      type="text"
                      value={edits.zip_code ?? data.zip_code ?? ''}
                      onChange={(e) => handleEdit(error.row_number, 'zip_code', e.target.value)}
                      className={hasError('zip_code') ? 'error' : ''}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>

              <div className="card-actions">
                <button
                  onClick={() => handleRevalidate(error.row_number)}
                  className="btn-primary"
                  disabled={isRevalidating || !editingRows[error.row_number]}
                >
                  {isRevalidating ? 'Validating...' : 'Revalidate This Address'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="action-buttons">
        <button
          onClick={handleRevalidateAll}
          className="btn-primary"
          disabled={isRevalidating}
        >
          {isRevalidating ? 'Revalidating...' : `Revalidate All (${errors.length})`}
        </button>
        <button
          onClick={handleSkip}
          className="btn-secondary"
        >
          Skip Invalid & Continue
        </button>
      </div>
    </div>
  );
}

export default ErrorCorrectionPage;